import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { BiContextProvidedValue, BiData } from '@wix/da-bi/pkg/events';
import PositionOnlyPopperPopover from '@wix/da-ds/pkg/popper/presets/PositionOnlyPopperPopover';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { StandardDeviationElementPlacement } from '../../ElementPlacementContext/variants/standard/types';
import { StandardDeviationElementPlacementContext } from '../../ElementPlacementContext/variants/standard/context';
import { usePopover } from '../../../Popovers/redux/hooks';
import { PopoverType } from '../../../Popovers/redux/types';
import AddToGroupPopover from '../../_partials/AddToGroupPopup';
import { ActionBarContext } from '../../ActionBarContext';
import AddToCollectionPopup from '../../_partials/AddToCollectionPopup';
import { getDeviationNotificationQueue } from '../../../DeviationPage/redux/helpers';
import { NotificationType } from '../../../Notifications';
import { pushAddToGroupModal, pushPrivateCollectionModal } from '../../../Modals/redux/actionCreators';
/**
 * A helper for our elements with multiple placements. Since they could be an array or a single {@link StandardDeviationElementPlacement}
 * value, we have to check for that.
 */ function isElementInMoreMenu(elementPlacementValue) {
    if (elementPlacementValue === StandardDeviationElementPlacement.NotIncluded) {
        return false;
    }
    return elementPlacementValue.includes(StandardDeviationElementPlacement.PrimaryBarMenu) || elementPlacementValue.includes(StandardDeviationElementPlacement.SecondaryBarMenu);
}
function usePrivateCollectionPopupAction(deviation) {
    var _popover_params;
    const { t } = useTranslation();
    const dispatchPushPrivateCollectionModal = useAction(pushPrivateCollectionModal);
    const elementPlacementMap = useContext(StandardDeviationElementPlacementContext);
    const actionBarType = useContext(ActionBarContext);
    const isMobile = useContext(MobileContext);
    const { popover, removePopover, togglePopover } = usePopover(actionBarType === 'primary' ? PopoverType.ADD_TO_PRIVATE_COLLECTION_PRIMARY : PopoverType.ADD_TO_PRIVATE_COLLECTION_SECONDARY);
    const notificationQueue = isMobile ? 'default' : getDeviationNotificationQueue(deviation);
    const handleClick = useCallback(()=>{
        if (isMobile) {
            var _popover_params;
            dispatchPushPrivateCollectionModal(deviation, NotificationType.CollectPrivate, (_popover_params = popover.params) === null || _popover_params === void 0 ? void 0 : _popover_params.ignorePreselectedCollections);
        } else {
            togglePopover();
        }
    }, [
        dispatchPushPrivateCollectionModal,
        popover.params,
        togglePopover,
        isMobile,
        deviation
    ]);
    if (elementPlacementMap.privateCollection !== StandardDeviationElementPlacement.PrimaryBarMenu) {
        return undefined;
    }
    return {
        // Mobile is handled by our usual modal systems
        popup: !isMobile && /*#__PURE__*/ React.createElement(PositionOnlyPopperPopover, {
            key: "private-collection",
            popoverContent: /*#__PURE__*/ React.createElement(AddToCollectionPopup, {
                isPrivateCollections: true,
                collectibleDeviation: deviation,
                notificationQueue: notificationQueue,
                ignorePreselectedCollections: (_popover_params = popover.params) === null || _popover_params === void 0 ? void 0 : _popover_params.ignorePreselectedCollections,
                onClose: removePopover
            }),
            offsetModifier: [
                0,
                20
            ],
            isVisible: popover.isOpen,
            placement: "bottom",
            onClickOutside: isMobile ? undefined : removePopover
        }),
        action: {
            value: 'collectPrivately',
            label: t('actionbar.collectPrivately.label'),
            onClick: handleClick,
            biData: BiData({
                evid: 334,
                creatorid: BiContextProvidedValue,
                sectionname: BiContextProvidedValue,
                typeid: BiContextProvidedValue,
                itemid: BiContextProvidedValue
            })
        },
        notificationType: NotificationType.CollectPrivate
    };
}
function useAddToGroupPopupAction(deviation) {
    const { t } = useTranslation();
    const isMobile = useContext(MobileContext);
    const dispatchPushAddToGroupModal = useAction(pushAddToGroupModal);
    const actionBarType = useContext(ActionBarContext);
    const elementPlacementMap = useContext(StandardDeviationElementPlacementContext);
    const { popover: addToGroupPopover, pushPopover: pushAddToGroupPopover, removePopover: removeAddToGroupPopover } = usePopover(actionBarType === 'primary' ? PopoverType.ADD_TO_GROUP_PRIMARY : PopoverType.ADD_TO_GROUP_SECONDARY);
    const handleClick = useCallback(()=>{
        if (isMobile) {
            dispatchPushAddToGroupModal(deviation, undefined);
        } else {
            pushAddToGroupPopover();
        }
    }, [
        isMobile,
        dispatchPushAddToGroupModal,
        pushAddToGroupPopover,
        deviation
    ]);
    return isElementInMoreMenu(elementPlacementMap.addToGroup) ? {
        popup: !isMobile && /*#__PURE__*/ React.createElement(PositionOnlyPopperPopover, {
            key: "add-to-group-popover",
            popoverContent: /*#__PURE__*/ React.createElement(AddToGroupPopover, {
                actionbarType: actionBarType,
                deviation: deviation,
                onClose: removeAddToGroupPopover
            }),
            isVisible: addToGroupPopover.isOpen,
            placement: "bottom",
            onClickOutside: removeAddToGroupPopover
        }),
        action: {
            value: 'addToGroup',
            label: t('actionbar.addToGroup'),
            onClick: handleClick,
            biData: BiData({
                evid: 340,
                deviationid: deviation.deviationId,
                sectionname: 'add_to_group'
            })
        },
        notificationType: NotificationType.AddToGroup
    } : undefined;
}
/**
 * Actions that require a popup to be displayed. Maps from our {@link StandardDeviationElementPlacementMap} keys to a
 * {@link MoreMenuPopupAction} or `undefined`. `undefined` means the action was set to appear elsewhere or nowhere.
 */ export function useActionsWithPopups(deviation) {
    return {
        addToGroup: useAddToGroupPopupAction(deviation),
        privateCollection: usePrivateCollectionPopupAction(deviation)
    };
}

//# sourceMappingURL=hooks.js.map